/**
 * Configurable props for `formly-required-documents-checklist`.
 *
 * The field was originally authored for the document-onboarding flow (#19108).
 * Every prop below is optional. Props with runtime defaults document them below,
 * and existing form-builder configurations can override those defaults.
 */
/**
 * Display of the checklist. `cards` is the existing card list and stays the default, so every
 * existing form configuration renders unchanged. `rows` is the compact row view from the onboarding
 * design (Figma 3802:165013), set per field in the form builder under "Weergave", like
 * `tagsDisplayMode` on the tags field. `compact` is the same card list in the compact density (status
 * on the title line, less padding) for a long list, with every card action and the "…" menu; unlike
 * `rows` it loses nothing. `tile` is the fixed-size status-tile card (proposal A "Statustegel",
 * DISTRODOT-PORTAL-MN): same affordances as `cards`, every card the same height whatever its state.
 */
export const DocumentsDisplayModeEnum = <const>{
  Cards: 'cards',
  Rows: 'rows',
  Compact: 'compact',
  Tile: 'tile'
};
export type DocumentsDisplayMode = (typeof DocumentsDisplayModeEnum)[keyof typeof DocumentsDisplayModeEnum];

export interface RequiredDocumentsChecklistProps {
  /**
   * @deprecated Use `instanceModuleCode`. Kept for backwards-compat with
   * existing database configs that already set this key.
   */
  moduleCode?: string;
  /**
   * Instance/upload-target module code — the status-source read by this checklist
   * (document-records; the raw file lives in the `documents` Uploads module and is
   * referenced from records via `bestand`).
   * Resolution order: `instanceModuleCode` → `moduleCode` → `'document-records'`.
   */
  instanceModuleCode?: string;
  /** Module code for the document-template source list. Default: `'document-templates'`. */
  templateModuleCode?: string;
  /**
   * Hoofdtype slug used only to scope instance records in the OData load filter
   * and the client-side `uploaded` computed. It is not written to new records.
   *
   * Explicit `null` disables hoofdtype scoping in both filters.
   *
   * Default: `null`.
   */
  instanceHoofdtypeSlug?: string | null;
  /** OData filter for loading template entries. Default: the onboarding `showInOnboarding` filter. */
  templateFilter?: string;
  /**
   * Back-ref field on the instance-target module pointing at the host entry. Default:
   * `'entry.entryId'`. When the host entry is itself the document anchor (no nested
   * `entry.company`/`entry.user` of its own), the back-ref key selects which kind of anchor the
   * host is: `'entry.company'`, `'entry.user'`, or `'entry.asset'` (bedrijfsmiddel-host).
   */
  moduleLinkedField?: string;
  /**
   * Kaarten of compacte regels. Default: `cards`. Zie {@link DocumentsDisplayMode}.
   */
  documentsDisplayMode?: DocumentsDisplayMode;
  /** Opt-out for the "all required documents uploaded" wizard validator. Default: `true`. */
  enforceUploadValidator?: boolean;
  /**
   * Accepted file extensions/mime-types for the card's dropzone. A template may override this per
   * document via its own `acceptedTypes`. Validation happens on drop as well as on pick — the
   * `accept` attribute only filters the native picker.
   */
  acceptedTypes?: string[];
  /**
   * Size ceiling in MB enforced on drop/pick, shared with `formly-field-upload-document`'s prop of
   * the same name (see `upload-validation.util.ts`). No dedicated form-builder toggle for this
   * field type yet — config-only. Default: `DEFAULT_MAX_FILE_SIZE_MB` (50).
   */
  maxFileSizeMb?: number;
}

/**
 * NOTE — target roles are not a prop. They are read from `formState.onboardingForm`
 * (`entry.settings.roles`), published by the onboarding page, because they belong to the
 * onboarding flow rather than to this field's configuration.
 */
